import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { API, toast } from '@/App';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Mesajınız başarıyla gönderildi!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Mesaj gönderilirken hata oluştu.');
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>İletişim - Ener Yazılım | Bizimle İletişime Geçin</title>
        <meta name="description" content="Projeleriniz hakkında konuşmak için bizimle iletişime geçin. E-posta, telefon veya iletişim formu ile bize ulaşabilirsiniz." />
        <meta name="keywords" content="iletişim, ener yazılım iletişim, teklif al, proje teklifi, yazılım danışmanlık" />
        <link rel="canonical" href="https://www.eneryazilim.com/iletisim" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eneryazilim.com/iletisim" />
        <meta property="og:title" content="İletişim - Ener Yazılım" />
        <meta property="og:description" content="Projeleriniz hakkında konuşmak için bizimle iletişime geçin." />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/iletisim" />
        <meta name="twitter:title" content="İletişim - Ener Yazılım" />
        <meta name="twitter:description" content="Projeleriniz hakkında konuşmak için bizimle iletişime geçin." />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - ContactPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "Ener Yazılım",
              "url": "https://www.eneryazilim.com",
              ...(settings?.legal_name ? { "legalName": settings.legal_name } : {}),
              ...(settings?.tax_number ? { "taxID": settings.tax_number, "vatID": settings.tax_number } : {}),
              ...(settings?.company_address ? {
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": settings.company_address,
                  "addressCountry": "TR"
                }
              } : {}),
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "email": settings?.company_email || "info@eneryazilim.com",
                "telephone": settings?.company_phone || "",
                "availableLanguage": "Turkish"
              }
            }
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="contact-title">
              <span className="gradient-text">İletişim</span>
            </h1>
            <p className="text-lg text-white/70">
              Projeleriniz hakkında konuşmak için bizimle iletişime geçin
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="pb-20" data-testid="contact-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="glass p-8 rounded-2xl" data-testid="contact-form-container">
              <h2 className="text-2xl font-bold mb-6">Mesaj Gönder</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Adınız Soyadınız"
                    required
                    data-testid="contact-name-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="E-posta Adresiniz"
                    required
                    data-testid="contact-email-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Telefon Numaranız (Opsiyonel)"
                    data-testid="contact-phone-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Konu"
                    required
                    data-testid="contact-subject-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Mesajınız"
                    required
                    rows={6}
                    data-testid="contact-message-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] hover:shadow-lg hover:shadow-[#FF6B35]/50 transition-all"
                >
                  {loading ? 'Gönderiliyor...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Gönder
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold mb-6">İletişim Bilgileri</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20">
                      <Mail className="w-6 h-6 text-[#FF6B35]" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">E-posta</h3>
                      <p className="text-white/60">{settings?.company_email || 'info@eneryazilim.com'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20">
                      <Phone className="w-6 h-6 text-[#FF6B35]" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Telefon</h3>
                      <p className="text-white/60">{settings?.company_phone || '+90 XXX XXX XX XX'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20">
                      <MapPin className="w-6 h-6 text-[#FF6B35]" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Adres</h3>
                      <p className="text-white/60">
                        {settings?.company_address || 'Türkiye'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h3 className="text-xl font-semibold mb-4">Çalışma Saatleri</h3>
                <div className="space-y-2 text-white/60">
                  <p>Pazartesi - Cuma: 09:00 - 18:00</p>
                  <p>Cumartesi: 10:00 - 16:00</p>
                  <p>Pazar: Kapalı</p>
                </div>
              </div>

              {/* Kurumsal Künye */}
              {(settings?.legal_name || settings?.tax_number || settings?.mersis_no) && (
                <div className="glass p-8 rounded-2xl" data-testid="legal-info">
                  <h3 className="text-xl font-semibold mb-4">Kurumsal Künye</h3>
                  <dl className="space-y-3 text-sm">
                    {settings.legal_name && (
                      <div>
                        <dt className="text-white/40">Ticari Unvan</dt>
                        <dd className="text-white/80" data-testid="legal-name">{settings.legal_name}</dd>
                      </div>
                    )}
                    {settings.company_address && (
                      <div>
                        <dt className="text-white/40">Adres</dt>
                        <dd className="text-white/80">{settings.company_address}</dd>
                      </div>
                    )}
                    {settings.tax_office && (
                      <div>
                        <dt className="text-white/40">Vergi Dairesi</dt>
                        <dd className="text-white/80">{settings.tax_office}</dd>
                      </div>
                    )}
                    {settings.tax_number && (
                      <div>
                        <dt className="text-white/40">Vergi Kimlik No (VKN)</dt>
                        <dd className="text-white/80" data-testid="legal-tax-number">{settings.tax_number}</dd>
                      </div>
                    )}
                    {settings.mersis_no && (
                      <div>
                        <dt className="text-white/40">MERSİS No</dt>
                        <dd className="text-white/80" data-testid="legal-mersis">{settings.mersis_no}</dd>
                      </div>
                    )}
                    {settings.trade_registry_no && (
                      <div>
                        <dt className="text-white/40">Ticaret Sicil No</dt>
                        <dd className="text-white/80">{settings.trade_registry_no}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;